import { HttpBackend, HttpClient, HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, finalize, from, shareReplay, switchMap, throwError } from 'rxjs';
import { canReplayAfterRefresh, markNotReplayedAfterRefresh } from '@lite-workspace/utils';
import { AuthState, clearAuth, readAuthState, redirectToIdentity, writeAuthState } from './auth';
import {
  bffLogout,
  buildAdminReimpersonateUrl,
  invalidateBffSession,
  isBffMode,
  isLegacyAuthDisabled,
  localIdentityDeadEnd,
  probeBffSession,
  redirectToAdminReimpersonate,
  redirectToBffLogin,
  redirectToLocalDeadEnd,
} from './bff';
import { EXPECTED_FORBIDDEN } from './http-context.token';
import { recheckIdentity } from './identity-watch';
import {
  activeClientSession,
  clearClientPreview,
  clearClientSession,
  currentClientSessionGeneration,
  inClientZone,
  markClientSessionRevoked,
  readClientPreview,
  readClientSession,
  redirectToClientReentry,
} from './client-session';

// Единственный 401, который означает «ключ заменили»: сервер шлёт его только при несовпадении
// версии ключа (PersonSessionRevokedError). Любой другой 401 — умершая/отвергнутая сессия, и
// объявлять его отзывом нельзя: ученик уходил искать новый PIN, которого никто не менял.
const PERSON_SESSION_REVOKED_ERROR_CODE = 'PERSON_SESSION_REVOKED';

function isPersonSessionRevoked(err: HttpErrorResponse): boolean {
  return (err.error as { errorCode?: string } | null)?.errorCode === PERSON_SESSION_REVOKED_ERROR_CODE;
}

// Not Router.url: a boot-time 401 fires before the first navigation, when it is still '/'.
function currentReturnUrl(): string {
  return window.location.pathname + window.location.search;
}

export const RETRIED = new HttpContextToken<boolean>(() => false);

// Dedupes concurrent 401s so only one PUT /api/auth/tokens is in-flight at a time.
let refreshing$: Observable<AuthState> | null = null;
// Обновление делится только внутри своего поколения: запрос после нового входа, подхватив refresh
// прошлой сессии, записал бы её токены поверх свежих.
let refreshingGeneration = -1;

// Один уход с документа: 401 приходят пачкой, clearAuth первого из них стирает доказательство
// ученической сессии, и поздний собрат перебил бы уже начатый возврат формой входа фотографа.
let leavingForLogin = false;

// «Назад» со страницы входа возвращает документ из bfcache с этим же модулем: без сброса флаг пережил бы
// уход, и следующий 401 в ожившей вкладке молча никуда не уводил бы.
function releaseLeavingOnRestore(event: PageTransitionEvent): void {
  if (event.persisted) leavingForLogin = false;
}

// Одно гашение на поколение: 401 отзыва приходят пачкой, а следующий вход открывает свою сессию,
// и её отзыв обязан гаситься снова.
let bffSessionDroppedGeneration = -1;

export function resetErrorInterceptorStateForTests(): void {
  refreshing$ = null;
  refreshingGeneration = -1;
  leavingForLogin = false;
  bffSessionDroppedGeneration = -1;
}

function getOrStartRefresh(bypassHttp: HttpClient): Observable<AuthState> {
  const generation = currentClientSessionGeneration();
  if (!refreshing$ || refreshingGeneration !== generation) {
    refreshingGeneration = generation;
    const state = readAuthState();
    // Обновлений может быть два сразу (своё у каждого поколения) — слот освобождает только своё,
    // иначе завершившееся прошлое стирает живое и следующий 401 шлёт третий PUT вместо общего.
    const started: Observable<AuthState> = bypassHttp
      .put<AuthState>('/api/auth/tokens', {
        accessToken: state?.accessToken,
        refreshToken: state?.refreshToken,
      })
      .pipe(
        finalize(() => {
          if (refreshing$ === started) refreshing$ = null;
        }),
        shareReplay(1),
      );
    refreshing$ = started;
  }

  return refreshing$;
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  // Refresh PUT uses a bare HttpClient (HttpBackend) to bypass this interceptor and avoid re-entry.
  const bypassHttp = new HttpClient(inject(HttpBackend));
  // Поколение снимаем до вылета запроса: его 401 судит ту сессию, при которой запрос ушёл,
  // а не ту, которую успел записать вход, пока ответ был в пути.
  const generation = currentClientSessionGeneration();

  // Запрос прошлого поколения: пока его 401 летел, ученик уже вошёл заново. Ни гасить сессию,
  // ни стирать её токены такой ответ не вправе — он судит чужую, уже мёртвую сессию.
  // Без привязки к зоне: вход ведёт ученика на верхнеуровневый /editor, и запоздалый ответ
  // догоняет его уже там; у фотографа поколение всегда 0 — для него проверка никогда не срабатывает.
  const clientSessionMoved = (): boolean => generation !== currentClientSessionGeneration();

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      // 403 при живой сессии — возможный след молчаливой смены identity (кто-то занял слот куки):
      // сверяем владельца с сервером; подтверждённая смена перезагрузит вкладку под новым именем.
      if (err.status === 403) {
        if (!req.context.get(EXPECTED_FORBIDDEN)) void recheckIdentity({ force: true });
        return throwError(() => err);
      }

      if (err.status !== 401) return throwError(() => err);
      if (clientSessionMoved()) return throwError(() => err);

      const hasLegacyTokens = !!readAuthState()?.accessToken;

      // Captured while the (rejected) bearer is still readable: clearAuth below wipes the sub the
      // student check compares against. A student on an editor/preview route (outside /client) must
      // re-enter through their own link — identity/BFF login is a photographer-only surface.
      const clientSession = inClientZone() ? null : activeClientSession();

      // reimpersonateUrl приходит только с пути «probe подтвердил смерть сессии»: строить его позже
      // уже нечем — invalidateBffSession() стирает память о кабинете, в котором админ работал.
      const redirectToLogin = (reimpersonateUrl?: string | null) => {
        // Вкладка просмотра «как клиент» живёт на сессии ФОТОГРАФА: её смерть закрывает просмотр и
        // ведёт на вход фотографа, а не на ученические ветки ниже. Возврат — туда, откуда просмотр
        // открыли, иначе после входа фотограф оказывался бы на ученическом экране без сессии.
        const preview = readClientPreview();
        if (preview) clearClientPreview();
        const returnUrl = preview ? (preview.returnUrl ?? `/projects/${preview.projectId}`) : currentReturnUrl();

        // Внутри /client редиректа нет. «Доступ обновился» — только когда сервер
        // подтвердил отзыв ключа; прочие 401 (протухшая сессия, отвергнутый алгоритм) уводят на
        // чистый повторный вход по собственной ссылке ученика — без ложного «ищи новый PIN».
        if (!preview && inClientZone()) {
          if (isPersonSessionRevoked(err)) {
            markClientSessionRevoked(generation);
            return;
          }

          const session = readClientSession();
          // Мёртвая, но НЕ отозванная сессия: ученик уезжает на чистый повторный вход по своей же
          // ссылке, а локальные следы стираются (перезагрузка не крутит тот же мёртвый цикл).
          // redirectToClientReentry сам гасит петли возвратов (takeClientReentryUrl) и отвечает,
          // ушли ли фактически: token-only сессия с исчерпанным анти-циклом вернуть некуда.
          if (session && redirectToClientReentry(session)) {
            clearAuth();
            clearClientSession();
            return;
          }

          // Возвращать некуда (сессия без ссылок или исчерпанный анти-цикл) — прежний экран
          // честнее тишины: очищенное состояние без единого сигнала читалось бы как зависание.
          // Мёртвый ключ при этом стираем: пережив клиентскую сессию, он встречает следующий вход
          // ученика вопросом «в этом браузере уже выполнен вход — например, фотографа».
          clearAuth();
          markClientSessionRevoked(generation);
          return;
        }

        // Вердикт первого 401 окончателен: у него ещё было чем отличить ученика от фотографа.
        if (leavingForLogin) return;
        leavingForLogin = true;
        // Обработчик один и тот же — повторный уход второго слушателя не добавит.
        window.addEventListener('pageshow', releaseLeavingOnRestore);

        // Живая клиентская сессия обрывает путь фотографа: у ученика нет ни email, ни пароля.
        // Просмотр — исключение: за записью стоит сам фотограф, и вход ему как раз доступен.
        if (clientSession && !preview) {
          redirectToClientReentry(clientSession);
          return;
        }

        // Сеанс «войти от имени» продлить нечем, а форма входа фотографа админу тупик: возвращаем его
        // в админку, она перевыпускает вход в тот же кабинет и на тот же экран (FKS-11956).
        if (reimpersonateUrl) redirectToAdminReimpersonate(reimpersonateUrl);
        else if (isBffMode()) redirectToBffLogin(returnUrl);
        // Локальный контур без BFF: за ID_ENDPOINT сессии нет — вместо немого ухода на пустой порт
        // показываем, чем её выпустить (FKS-12060).
        else if (localIdentityDeadEnd()) redirectToLocalDeadEnd(returnUrl);
        else redirectToIdentity(returnUrl);
      };

      // Отзыв ключа — приговор самому ключу, а не транспорту сессии, поэтому судим по коду сервера
      // раньше обоих транспортных путей: refresh заменённый ключ не воскресит, а probe ответит
      // 'alive' (bff_sid переживает регенерацию ПИН/ссылки) и утопит ревок-экран (FKS-11760).
      if (isPersonSessionRevoked(err)) {
        clearAuth();
        // Живой bff_sid без клиентской сессии приложение читает как ЧУЖОЙ аккаунт и встречает
        // возврат ученика вопросом «в этом браузере уже выполнен вход — например, фотографа».
        // Отозванный ключ хоронит и выпущенную по нему сессию.
        if (isBffMode() && bffSessionDroppedGeneration !== generation) {
          bffSessionDroppedGeneration = generation;
          void bffLogout();
        }

        redirectToLogin();
        return throwError(() => err);
      }

      // BFF-фотограф (localStorage пуст) ИЛИ hard cutover-флаг: фоновый 401 ≠ мёртвая
      // сессия — подтверждаем probe'ом, а не прежним legacy-refresh мостом. activeClientSession()
      // остаётся исключением даже под cutover — PIN-клиент protected-проекта не часть миграции.
      if (isBffMode() && (!hasLegacyTokens || (isLegacyAuthDisabled() && !activeClientSession()))) {
        return from(probeBffSession()).pipe(
          switchMap(verdict => {
            if (verdict === 'dead') {
              const reimpersonateUrl: string | null = buildAdminReimpersonateUrl(currentReturnUrl());
              invalidateBffSession();
              redirectToLogin(reimpersonateUrl);
            }

            // 'alive' и 'unknown' намеренно падают сквозь без действия — при 'unknown' (probe сам
            // недоступен) fail-open, как в admin: не редиректим по неподтверждённому подозрению.
            return throwError(() => err);
          }),
        );
      }

      // Легаси-сессия без cutover-исключения выше (фотограф до миграции / студ / координатор
      // вне BFF-режима, или LEGACY_AUTH_DISABLED ещё выключен): прежний refresh-путь;
      // точка миграции — refresh умер → в BFF-режиме уходим на BFF-логин, не на identity.

      if (req.url.includes('/api/auth/tokens') || req.context.get(RETRIED)) {
        clearAuth();
        redirectToLogin();
        return throwError(() => err);
      }

      return getOrStartRefresh(bypassHttp).pipe(
        // Сессию гасит отказ самого refresh; ошибка повторённого запроса — нет: 403/500 после удачного
        // refresh — беда этого запроса, а не повод выкидывать фотографа на вход.
        catchError(() => {
          // Вход мог случиться, пока refresh был в пути: тогда эта цепочка уже про прошлую сессию.
          if (clientSessionMoved()) return throwError(() => err);
          clearAuth();
          redirectToLogin();
          return throwError(() => err);
        }),
        switchMap(newState => {
          // Обновлённые токены прошлой сессии не должны лечь поверх токенов свежего входа.
          if (clientSessionMoved()) return throwError(() => err);
          writeAuthState({ accessToken: newState.accessToken, refreshToken: newState.refreshToken });
          if (!canReplayAfterRefresh(req.method)) return throwError(() => markNotReplayedAfterRefresh(err));

          const retried = req.clone({
            setHeaders: { Authorization: `Bearer ${newState.accessToken}` },
            context: req.context.set(RETRIED, true),
          });
          return next(retried).pipe(
            catchError((retryErr: HttpErrorResponse) => {
              // Только повторный 401 значит, что и свежий токен не годится, — сессия мертва.
              if (retryErr.status === 401 && !clientSessionMoved()) {
                clearAuth();
                redirectToLogin();
              }

              return throwError(() => retryErr);
            }),
          );
        }),
      );
    }),
  );
};
